/**
 * Notification Management Page - ClientAdmin and SystemAdmin only
 * Create and manage notifications for organization users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Send, Users, User, Globe, Loader2, AlertCircle,
  CheckCircle, X, Trash2, RefreshCw, Info, AlertTriangle,
  CheckCircle2, Megaphone, ShieldCheck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { getErrorMessage } from '../services/apiService';
import { listClientUsers, ClientUser } from '../services/clientService';
import {
  createSystemNotification,
  createSystemAdminsNotification,
  createClientNotification,
  createUserNotification,
  getCreatedNotifications,
  deactivateNotification,
  AdminNotification,
  CreateNotificationRequest,
  NotificationType,
  NotificationScope
} from '../services/notificationService';

type TabType = 'create' | 'manage';

const NotificationManagement: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const isClientAdmin = user?.role === 'ClientAdmin';
  const isSystemAdmin = user?.isSystemAdmin === true;

  // Redirect if not authorized
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
    } else if (!isClientAdmin && !isSystemAdmin) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate, isClientAdmin, isSystemAdmin]);

  const [activeTab, setActiveTab] = useState<TabType>('create');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState<NotificationType>('Info');
  const [scope, setScope] = useState<NotificationScope>('Client');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [actionLabel, setActionLabel] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Organization users (for user-targeted notifications)
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Created notifications
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showDeactivated, setShowDeactivated] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!user?.clientId) return;

    try {
      setLoadingUsers(true);
      const userData = await listClientUsers(user.clientId);
      setUsers(userData.filter(u => u.isActive)); // Only show active users
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, [user?.clientId]);

  const loadNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const data = await getCreatedNotifications(showDeactivated);
      setNotifications(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingNotifications(false);
    }
  }, [showDeactivated]);

  useEffect(() => {
    if (user?.clientId) {
      loadUsers();
    }
  }, [user?.clientId, loadUsers]);

  useEffect(() => {
    if (activeTab === 'manage') {
      loadNotifications();
    }
  }, [activeTab, loadNotifications]);

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setNotificationType('Info');
    setScope('Client');
    setSelectedUserId('');
    setExpiresAt('');
    setActionUrl('');
    setActionLabel('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!title.trim() || !message.trim()) {
      setError('Title and message are required');
      return;
    }

    if (scope === 'User' && !selectedUserId) {
      setError('Please select a user for individual notifications');
      return;
    }

    const request: CreateNotificationRequest = {
      title: title.trim(),
      message: message.trim(),
      type: notificationType,
      actionUrl: actionUrl.trim() || undefined,
      actionLabel: actionLabel.trim() || undefined,
      expiresAt: expiresAt || undefined
    };

    try {
      setIsSubmitting(true);

      if (scope === 'System') {
        await createSystemNotification(request);
      } else if (scope === 'SystemAdmins') {
        await createSystemAdminsNotification(request);
      } else if (scope === 'Client' && user?.clientId) {
        await createClientNotification(user.clientId, request);
      } else if (scope === 'User') {
        await createUserNotification(selectedUserId, request);
      }

      setSuccess('Notification created successfully');
      resetForm();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async (notificationId: string) => {
    try {
      await deactivateNotification(notificationId);
      setSuccess('Notification deactivated');
      loadNotifications();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'Info': return <Info className="w-4 h-4 text-blue-500" />;
      case 'Warning': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'Error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'Success': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'Announcement': return <Megaphone className="w-4 h-4 text-purple-500" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getScopeIcon = (scopeValue: NotificationScope) => {
    switch (scopeValue) {
      case 'System': return <Globe className="w-4 h-4" />;
      case 'Client': return <Users className="w-4 h-4" />;
      case 'User': return <User className="w-4 h-4" />;
      case 'SystemAdmins': return <ShieldCheck className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f0]">
      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[#0f172a]">Notification Management</h1>
            <p className="text-[#64748b] mt-1">
              Send notifications to your organization{isSystemAdmin ? ' or all users' : ''}
            </p>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-green-700">{success}</span>
              <button
                onClick={() => setSuccess('')}
                className="ml-auto text-green-500 hover:text-green-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-white/50 p-1 rounded-lg border border-black/[0.06]">
            <button
              onClick={() => setActiveTab('create')}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'create'
                  ? 'bg-white text-[#c41e3a] shadow-sm'
                  : 'text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <Send className="w-4 h-4 inline-block mr-2" />
              Create Notification
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'manage'
                  ? 'bg-white text-[#c41e3a] shadow-sm'
                  : 'text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <Bell className="w-4 h-4 inline-block mr-2" />
              My Notifications
            </button>
          </div>

          {/* Create Tab */}
          {activeTab === 'create' && (
            <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Scope Selection */}
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-2">
                    Send To
                  </label>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => setScope('Client')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                        scope === 'Client'
                          ? 'bg-[#c41e3a]/10 border-[#c41e3a] text-[#c41e3a]'
                          : 'bg-white border-black/[0.08] text-[#475569] hover:border-[#c41e3a]/50'
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      Organization
                    </button>
                    <button
                      type="button"
                      onClick={() => setScope('User')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                        scope === 'User'
                          ? 'bg-[#c41e3a]/10 border-[#c41e3a] text-[#c41e3a]'
                          : 'bg-white border-black/[0.08] text-[#475569] hover:border-[#c41e3a]/50'
                      }`}
                    >
                      <User className="w-4 h-4" />
                      Individual User
                    </button>
                    <button
                      type="button"
                      onClick={() => setScope('SystemAdmins')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                        scope === 'SystemAdmins'
                          ? 'bg-[#c41e3a]/10 border-[#c41e3a] text-[#c41e3a]'
                          : 'bg-white border-black/[0.08] text-[#475569] hover:border-[#c41e3a]/50'
                      }`}
                    >
                      <ShieldCheck className="w-4 h-4" />
                      System Admins
                    </button>
                    {isSystemAdmin && (
                      <button
                        type="button"
                        onClick={() => setScope('System')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          scope === 'System'
                            ? 'bg-[#c41e3a]/10 border-[#c41e3a] text-[#c41e3a]'
                            : 'bg-white border-black/[0.08] text-[#475569] hover:border-[#c41e3a]/50'
                        }`}
                      >
                        <Globe className="w-4 h-4" />
                        System-wide
                      </button>
                    )}
                  </div>
                  {scope === 'SystemAdmins' && (
                    <p className="mt-2 text-xs text-[#64748b]">
                      Sends to all AAS system administrators
                    </p>
                  )}
                </div>

                {/* User Selection (for individual notifications) */}
                {scope === 'User' && (
                  <div>
                    <label className="block text-sm font-medium text-[#475569] mb-2">
                      Select User
                    </label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a] transition-all"
                      disabled={loadingUsers}
                    >
                      <option value="">Select a user...</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={200}
                    placeholder="Notification title"
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a] transition-all"
                    required
                  />
                  <p className="text-xs text-[#94a3b8] mt-1">{title.length}/200 characters</p>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={2000}
                    rows={4}
                    placeholder="Notification message"
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a] transition-all resize-none"
                    required
                  />
                  <p className="text-xs text-[#94a3b8] mt-1">{message.length}/2000 characters</p>
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-2">
                    Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(['Info', 'Warning', 'Error', 'Success', 'Announcement'] as NotificationType[]).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setNotificationType(type)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${
                          notificationType === type
                            ? 'bg-[#0f172a] text-white'
                            : 'bg-white border border-black/[0.08] text-[#475569] hover:bg-black/[0.02]'
                        }`}
                      >
                        {getTypeIcon(type)}
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional Fields */}
                <div className="border-t border-black/[0.06] pt-6">
                  <h3 className="text-sm font-medium text-[#475569] mb-4">Optional Settings</h3>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm text-[#64748b] mb-2">
                        Expiration Date
                      </label>
                      <input
                        type="datetime-local"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#64748b] mb-2">
                        Action URL
                      </label>
                      <input
                        type="url"
                        value={actionUrl}
                        onChange={(e) => setActionUrl(e.target.value)}
                        placeholder="https://..."
                        className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a] transition-all"
                      />
                    </div>
                    {actionUrl && (
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#64748b] mb-2">
                          Action Button Label
                        </label>
                        <input
                          type="text"
                          value={actionLabel}
                          onChange={(e) => setActionLabel(e.target.value)}
                          maxLength={100}
                          placeholder="e.g., View Details, Learn More"
                          className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a] transition-all"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-2.5 bg-[#c41e3a] text-white rounded-lg hover:bg-[#a31830] transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Send Notification
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Manage Tab */}
          {activeTab === 'manage' && (
            <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[#0f172a]">Your Notifications</h2>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-[#64748b]">
                    <input
                      type="checkbox"
                      checked={showDeactivated}
                      onChange={(e) => setShowDeactivated(e.target.checked)}
                      className="rounded border-black/[0.08]"
                    />
                    Show deactivated
                  </label>
                  <button
                    onClick={loadNotifications}
                    disabled={loadingNotifications}
                    className="flex items-center gap-2 text-sm text-[#64748b] hover:text-[#0f172a]"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingNotifications ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              {loadingNotifications ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#c41e3a]" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12 text-[#64748b]">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No notifications created yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 rounded-lg border transition-all ${
                        notification.isActive
                          ? 'bg-white border-black/[0.08]'
                          : 'bg-gray-50 border-gray-200 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {getTypeIcon(notification.type)}
                            <h3 className="font-medium text-[#0f172a] truncate">
                              {notification.title}
                            </h3>
                            {!notification.isActive && (
                              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                Deactivated
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#64748b] line-clamp-2 mb-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-[#94a3b8]">
                            <span className="flex items-center gap-1">
                              {getScopeIcon(notification.scope)}
                              {notification.scope === 'User' && notification.targetUserName
                                ? notification.targetUserName
                                : notification.scope}
                            </span>
                            <span>{formatDate(notification.createdAt)}</span>
                            {notification.expiresAt && (
                              <span className="text-amber-600">
                                Expires: {formatDate(notification.expiresAt)}
                              </span>
                            )}
                          </div>
                        </div>
                        {notification.isActive && (
                          <button
                            onClick={() => handleDeactivate(notification.id)}
                            className="p-2 text-[#64748b] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Deactivate notification"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotificationManagement;
